import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Map simple language codes to more specific locales for nicer month names
const localeMap = {
  en: 'en-US',
  es: 'es-ES',
};

/**
 * useFormatDate
 * Returns a stable formatter function to format dates according to the current i18n language.
 *
 * @param {Object} defaultOptions - Intl.DateTimeFormat options to use by default.
 * @returns {(dateInput: string|number|Date, overrideOptions?: Object) => string}
 */
export default function useFormatDate(defaultOptions = { year: 'numeric', month: 'short', day: 'numeric' }) {
  const { i18n } = useTranslation();

  const resolvedLocale = useMemo(() => {
    const lang = i18n?.language || 'en';
    const base = lang.split('-')[0];
    return localeMap[base] || lang || 'en-US';
  }, [i18n?.language]);

  const baseOptions = useMemo(() => ({ ...defaultOptions }), [defaultOptions]);

  const formatDate = useCallback((dateInput, overrideOptions = {}) => {
    if (!dateInput) return '';
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(d)) return '';
    const opts = { ...baseOptions, ...overrideOptions };
    try {
      return d.toLocaleDateString(resolvedLocale, opts);
    } catch {
      return d.toLocaleDateString('en-US', opts);
    }
  }, [resolvedLocale, baseOptions]);

  return formatDate;
}
